<template>
  <div class="page-wrapper">
    <!-- Header -->
    <StudentHeader />

    <!-- ✅ Pending Post-Test Banner removed - now handled globally in App.vue -->

    <div class="find-tutors-container">
      <!-- Main Header -->
      <div class="main-header">
        <h1 class="main-title">Find Your Perfect Tutor</h1>
        <p class="main-subtitle">
          Discover qualified tutors matched to your learning needs using our smart recommendation
          system.
        </p>
      </div>

      <!-- Student Status Banner -->
      <StudentStatusBanner :dismissible="true" />

      <!-- Search Section -->
      <TutorSearchBar
        v-model:searchQuery="searchQuery"
        v-model:selectedSubject="selectedSubjectFilter"
        v-model:minRating="minRatingFilter"
        :availableSubjects="availableSubjects"
        @search="handleSearch"
        @filter="handleFilter"
        @clear="handleClear"
      />

      <!-- Recommendations Section -->
      <TutorRecommendations
        ref="recommendationsRef"
        :tutors="recommendedTutors"
        :isLoading="isLoadingRecommendations"
        :canBookSessions="canBookSessions"
        :canMessage="canMessage"
        :hasBlockingPendingPostTest="hasBlockingPendingPostTest"
        @openProfile="openProfileModal"
        @sendMessage="goToMessages"
        @bookSession="goToBookSession"
        @refresh="handleRefreshRecommendations"
      />

      <!-- Available Tutors Section -->
      <AvailableTutors
        :tutors="paginatedTutors"
        :isLoading="isLoadingAllTutors"
        :currentPage="currentPage"
        :totalPages="totalPages"
        :itemsPerPage="itemsPerPage"
        :totalItems="totalItems"
        :canBookSessions="canBookSessions"
        :canMessage="canMessage"
        :hasBlockingPendingPostTest="hasBlockingPendingPostTest"
        @openProfile="openProfileModal"
        @sendMessage="goToMessages"
        @bookSession="goToBookSession"
        @refresh="handleRefreshAllTutors"
        @pageChange="handlePageChange"
        @itemsPerPageChange="handleItemsPerPageChange"
      />
    </div>

    <!-- Profile Modal -->
    <TutorProfileModal
      :open="showProfileModal"
      :tutor="selectedTutor"
      :hasBlockingPendingPostTest="hasBlockingPendingPostTest"
      :pendingPostTest="pendingPostTest"
      @close="showProfileModal = false"
    />

    <!-- File Report Component -->
    <FileReport />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { auth, db } from "@/firebase";
import { doc, getDoc, collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import axios from "axios";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";

// Components
import StudentHeader from "@/components/StudentHeader.vue";
import Footer from "@/components/Footer.vue";
import TutorSearchBar from "@/components/Student/TutorSearchBar.vue";
import TutorRecommendations from "@/components/Student/TutorRecommendations.vue";
import AvailableTutors from "@/components/Student/AvailableTutors.vue";
import TutorProfileModal from "@/components/Student/TutorProfileModal.vue";
import StudentStatusNotice from "@/components/Student/StudentStatusNotice.vue";
import StudentStatusBanner from "@/components/Student/StudentStatusBanner.vue";
import FileReport from "@/components/FileReport.vue";
// ✅ PendingPostTestBanner import removed - now handled globally in App.vue

// Services
import { messagingPermissionService } from "@/services/messagingPermissionService";
import { conversationService } from "@/services/conversationService";

// Composables
import { useStudentStatus } from "@/composables/useStudentStatus";
import { usePendingPostTest } from "@/composables/usePendingPostTest";
import Swal from "sweetalert2";

// Set page metadata for SEO
const setPageMetadata = () => {
  // Update page title
  document.title = "ElevatED - Find Tutors";

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    document.head.appendChild(metaDescription);
  }
  metaDescription.content =
    "Find qualified tutors on ElevatED. Browse expert tutors, view ratings and reviews, filter by subject, and schedule personalized tutoring appointments. Smart AI-powered tutor recommendations for Senior High School students.";

  // Add Open Graph tags for social sharing
  let ogTitle = document.querySelector('meta[property="og:title"]');
  if (!ogTitle) {
    ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    document.head.appendChild(ogTitle);
  }
  ogTitle.content = "Find Expert Tutors - ElevatED";

  let ogDescription = document.querySelector('meta[property="og:description"]');
  if (!ogDescription) {
    ogDescription = document.createElement("meta");
    ogDescription.setAttribute("property", "og:description");
    document.head.appendChild(ogDescription);
  }
  ogDescription.content =
    "Discover qualified tutors matched to your learning needs. View profiles, ratings, and schedule appointments instantly.";
};

// Router for navigation
const router = useRouter();

// Student status composable
const { canBookSessions, canMessage } = useStudentStatus();

// Pending post-test check
const { hasBlockingPendingPostTest, nextPendingPostTest: pendingPostTest } = usePendingPostTest();

// Reactive data
const searchQuery = ref("");
const selectedSubjectFilter = ref("");
const minRatingFilter = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(4);
const totalItems = ref(0);
const recommendedTutorsData = ref([]);
const allTutorsData = ref([]);
const allTutorsUnfiltered = ref([]); // Keep unfiltered data
const studentSubjects = ref([]);
const isLoading = ref(true); // Keep for initial load
const isLoadingRecommendations = ref(true); // Start as true for initial load
const isLoadingAllTutors = ref(true); // Start as true for initial load
const showProfileModal = ref(false);
const selectedTutor = ref(null);
const error = ref(null);
const recommendationsRef = ref(null); // Reference to recommendations component

// Backend API URL
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://elevated-backend-xyaskmbwha-uc.a.run.app/api";

// Available subjects for filtering
const availableSubjects = [
  "Oral Communication",
  "Reading and Writing",
  "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
  "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  "21st Century Literature from the Philippines and the World",
  "General Mathematics",
  "Statistics and Probability",
  "Earth and Life Science",
  "Physical Science",
  "Earth Science",
];

// Filter function
const applyFilters = (tutors) => {
  let filtered = [...tutors];

  // Apply search query (name or subject)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter((tutor) => {
      const nameMatch = tutor.name.toLowerCase().includes(query);
      const subjectMatch = tutor.subjects.some((subject) => subject.toLowerCase().includes(query));
      return nameMatch || subjectMatch;
    });
  }

  // Apply subject filter
  if (selectedSubjectFilter.value) {
    filtered = filtered.filter((tutor) =>
      tutor.subjects.some(
        (subject) => subject.toLowerCase() === selectedSubjectFilter.value.toLowerCase()
      )
    );
  }

  // Apply minimum rating filter
  if (minRatingFilter.value > 0) {
    filtered = filtered.filter((tutor) => tutor.ratingValue >= minRatingFilter.value);
  }

  return filtered;
};

// Computed properties
const recommendedTutors = computed(() => {
  return recommendedTutorsData.value.slice(0, 2); // Top 2 recommendations
});

const availableTutors = computed(() => {
  return applyFilters(allTutorsData.value); // Apply filters to all tutors
});

const paginatedTutors = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return availableTutors.value.slice(start, end);
});

const totalPages = computed(() => Math.ceil(availableTutors.value.length / itemsPerPage.value));

// Methods
const openProfileModal = (tutor) => {
  selectedTutor.value = tutor;
  showProfileModal.value = true;
};

const goToMessages = async (tutor) => {
  try {
    logInfo("Starting conversation with tutor from FindTutorsView", {
      tutorId: tutor.id,
      tutorName: tutor.name,
    });

    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Please log in to send messages");
      return;
    }

    // Check if student can message this tutor (this is the authoritative check)
    const canMessageResult = await messagingPermissionService.canUsersMessage(
      currentUser.uid,
      tutor.id,
      "student"
    );

    if (!canMessageResult.canMessage) {
      alert(canMessageResult.reason || "Unable to message this tutor");
      return;
    }

    // Create conversation between student and tutor
    const conversationResult = await conversationService.createConversation(
      currentUser.uid, // studentId
      tutor.id, // tutorId
      undefined // no sessionId for direct messaging
    );

    if (!conversationResult.success) {
      alert(conversationResult.reason || "Unable to create conversation");
      return;
    }

    // Note: CometChat initialization will be handled by StudentMessagesView
    // We just create the conversation and navigate - the messaging view will handle CometChat setup

    // Navigate to student messages view with the specific conversation
    // The messaging view will handle creating the conversation if needed
    router.push({
      path: "/student-messages",
      query: {
        conversation: conversationResult.conversationId,
        contact: tutor.id,
      },
    });

    logInfo("Successfully navigated to messaging with tutor", {
      tutorId: tutor.id,
      conversationId: conversationResult.conversationId,
    });
  } catch (error) {
    logError("Error starting conversation with tutor", error);
    
    // Provide more specific error messages based on the error type
    const errorMessage = error?.code === 'ERR_UID_NOT_FOUND' || error?.code === 'ERR_BAD_REQUEST'
      ? "There was an issue setting up the chat. You can still send messages - the tutor will see them when they log in."
      : "Failed to start conversation. Please try again.";
    
    alert(errorMessage);
  }
};

const goToBookSession = async (tutor) => {
  // Check if student can schedule appointments
  if (!canBookSessions.value) {
    alert("Your account must be verified before you can schedule appointments.");
    return;
  }

  // Check for pending post-test blocking
  if (hasBlockingPendingPostTest.value && pendingPostTest.value) {
    await Swal.fire({
      icon: "warning",
      title: "⚠️ Pending Post-Test",
      html: `
        <div class="text-left">
          <p class="mb-3"><strong>You need to complete your post-test before scheduling new appointments.</strong></p>
          <div class="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-3">
            <p class="mb-2"><strong>📚 Subject:</strong> <span class="text-gray-700">${
              pendingPostTest.value.sessionSubject
            }</span></p>
            <p class="mb-2"><strong>👨‍🏫 Tutor:</strong> <span class="text-gray-700">${
              pendingPostTest.value.tutorName
            }</span></p>
            <p><strong>📅 Session Date:</strong> <span class="text-gray-700">${new Date(
              pendingPostTest.value.sessionDate.toDate()
            ).toLocaleDateString()}</span></p>
          </div>
          <p class="text-sm text-gray-600">Complete your post-test to continue scheduling appointments.</p>
        </div>
      `,
      confirmButtonText: "OK, I'll complete it now",
    });
    return;
  }

  router.push({
    name: "book-session-with-tutor",
    params: { tutorId: tutor.id },
  });
};

const handleSearch = (query) => {
  searchQuery.value = query;
  currentPage.value = 1; // Reset to first page when searching
  totalItems.value = availableTutors.value.length;
  
  // Collapse recommendations when searching
  if (query.trim() && recommendationsRef.value) {
    recommendationsRef.value.collapseRecommendations();
  }
};

const handleFilter = (filters) => {
  // Filters object should contain: { subject, minRating, etc }
  if (filters.subject !== undefined) {
    selectedSubjectFilter.value = filters.subject;
  }
  if (filters.minRating !== undefined) {
    minRatingFilter.value = filters.minRating;
  }
  currentPage.value = 1; // Reset to first page when filtering
  totalItems.value = availableTutors.value.length;
  
  // Collapse recommendations when applying filters
  if ((filters.subject || filters.minRating > 0) && recommendationsRef.value) {
    recommendationsRef.value.collapseRecommendations();
  }
};

const handleClear = () => {
  searchQuery.value = "";
  selectedSubjectFilter.value = "";
  minRatingFilter.value = 0;
  currentPage.value = 1;
  totalItems.value = allTutorsData.value.length;
};

const handlePageChange = (page) => {
  currentPage.value = page;
};

const handleItemsPerPageChange = (items) => {
  itemsPerPage.value = items;
  currentPage.value = 1;
};

// Handle refresh for recommendations only
const handleRefreshRecommendations = async () => {
  logInfo("🔄 Refreshing recommendations only...");
  isLoadingRecommendations.value = true;

  try {
    // Clear the cache to force fresh fetch
    const CACHE_KEY = "elevateED_tutor_recommendations";
    sessionStorage.removeItem(CACHE_KEY);

    // Add minimum delay for visual feedback
    const [freshData] = await Promise.all([
      loadFreshData(true), // Pass true to force refresh backend cache
      new Promise((resolve) => setTimeout(resolve, 800)), // Min 800ms for visual feedback
    ]);

    logInfo("✅ Recommendations refreshed");
  } catch (err) {
    logError("❌ Error refreshing recommendations:", err);
    error.value = "Failed to refresh recommendations";
  } finally {
    isLoadingRecommendations.value = false;
  }
};

// Handle refresh for all tutors only (from Firestore directly)
const handleRefreshAllTutors = async () => {
  logInfo("🔄 Refreshing all tutors from Firestore...");
  isLoadingAllTutors.value = true;

  try {
    // Add minimum delay for visual feedback
    const [freshTutors] = await Promise.all([
      loadAllTutorsFromFirestore(), // Load directly from Firestore
      new Promise((resolve) => setTimeout(resolve, 800)), // Min 800ms for visual feedback
    ]);

    // Filter tutors with availability and format data
    const tutorsWithAvailability = await Promise.all(
      freshTutors.map(async (tutor) => {
        const hasAvailability = await checkTutorHasAvailability(tutor.id);
        return hasAvailability ? formatTutorData(tutor) : null;
      })
    );

    // Update only the all tutors data
    allTutorsData.value = tutorsWithAvailability.filter((t) => t !== null);
    totalItems.value = allTutorsData.value.length;

    logInfo(`✅ All tutors refreshed: ${allTutorsData.value.length} tutors`);
  } catch (err) {
    logError("❌ Error refreshing all tutors:", err);
    error.value = "Failed to refresh tutors";
  } finally {
    isLoadingAllTutors.value = false;
  }
};

// Calculate recommendation score for a tutor
const calculateTutorScore = (tutorSubjects, tutorRating, tutorSessions, studentSubjs) => {
  // 1. Subject Match Score (40 points max - 10 per match, up to 4)
  const studentLower = studentSubjs.map((s) => s.toLowerCase().trim());
  const tutorLower = tutorSubjects.map((s) => s.toLowerCase().trim());
  const matches = studentLower.filter((s) => tutorLower.includes(s)).length;
  const subjectScore = Math.min(matches * 10, 40);

  // 2. Rating Score (30 points max - rating * 6)
  const ratingScore = (tutorRating || 0) * 6;

  // 3. Experience Score (30 points max - based on sessions)
  let experienceScore = 0;
  if (tutorSessions === 0) {
    experienceScore = 0;
  } else if (tutorSessions === 1) {
    experienceScore = 5;
  } else if (tutorSessions < 10) {
    experienceScore = 5 + (tutorSessions - 1) * 1.11;
  } else if (tutorSessions < 50) {
    experienceScore = 15 + ((tutorSessions - 10) / 40) * 8;
  } else {
    experienceScore = Math.min(23 + ((tutorSessions - 50) / 50) * 7, 30);
  }

  const totalScore = subjectScore + ratingScore + experienceScore;

  return {
    total: totalScore,
    subjectScore,
    ratingScore,
    experienceScore,
    matches,
  };
};

// Helper function to format tutor data
const formatTutorData = (tutor) => {
  let subjects = tutor.subjects || ["General Tutoring"];
  if (typeof subjects === "string") {
    subjects = subjects.includes(",") ? subjects.split(",").map((s) => s.trim()) : [subjects];
  }

  const nameParts = [tutor.firstName, tutor.lastName, tutor.suffix].filter(Boolean);
  const name = nameParts.join(" ").trim() || "Unknown Tutor";

  // Backend returns 'rating' field which is sourced from averageRating in Firestore
  const rating = tutor.rating || 0;

  return {
    id: tutor.id,
    name: name,
    rating: rating ? `${Number(rating).toFixed(1)}/5.0` : "No ratings yet",
    ratingValue: rating || 0,
    subjects: subjects,
    description: tutor.bio || "Tutor dedicated to helping students succeed.",
    availability: "Available for Appointments",
    education: tutor.education || "Qualified educator",
    experience: tutor.experienceYears
      ? `${tutor.experienceYears} ${tutor.experienceYears === 1 ? "year" : "years"} of experience`
      : "Professional tutoring experience",
    experienceYears: tutor.experienceYears || 0,
    specialties: subjects,
    languages: tutor.languages || ["English"],
    isAvailable: true,
    email: tutor.email,
    hourlyRate: tutor.hourlyRate || "Contact for rates",
    totalSessions: tutor.totalSessions || 0,
    profileImage: tutor.profileImage || null,
    recommendationScore: tutor.recommendationScore || 0,
    scoreBreakdown: tutor.scoreBreakdown || null,
  };
};

// Check if tutor has availability data
const checkTutorHasAvailability = async (tutorId) => {
  try {
    const availabilityRef = collection(db, "users", tutorId, "availability");
    const availabilitySnap = await getDocs(availabilityRef);
    return !availabilitySnap.empty;
  } catch (error) {
    logError(`Error checking availability for tutor ${tutorId}:`, error);
    return false;
  }
};

// Populate tutor data from API response and filter by availability
const populateTutorData = async (data) => {
  // Filter tutors with availability in parallel
  const recommendationsWithAvailability = await Promise.all(
    data.recommendations.map(async (tutor) => {
      const hasAvailability = await checkTutorHasAvailability(tutor.id);
      return hasAvailability ? formatTutorData(tutor) : null;
    })
  );

  const allTutorsWithAvailability = await Promise.all(
    data.allTutors.map(async (tutor) => {
      const hasAvailability = await checkTutorHasAvailability(tutor.id);
      return hasAvailability ? formatTutorData(tutor) : null;
    })
  );

  // Remove null entries (tutors without availability)
  recommendedTutorsData.value = recommendationsWithAvailability.filter((t) => t !== null);
  allTutorsData.value = allTutorsWithAvailability.filter((t) => t !== null);
  studentSubjects.value = data.studentSubjects || [];
  totalItems.value = allTutorsData.value.length;

  logInfo(
    `✅ Filtered tutors - Recommendations: ${recommendedTutorsData.value.length}, All: ${allTutorsData.value.length}`
  );
};

// Load tutors from BACKEND API with client-side caching
const loadTutors = async (useCache = true) => {
  try {
    // Check client-side cache first (5 min cache)
    const CACHE_KEY = "elevateED_tutor_recommendations";
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    if (useCache) {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;

          if (age < CACHE_DURATION) {
            logInfo("✅ Using cached data (age:", Math.round(age / 1000), "sec)");
            await populateTutorData(data);
            isLoading.value = false;
            isLoadingRecommendations.value = false;
            isLoadingAllTutors.value = false;

            // Refresh in background if cache is older than 3 minutes
            if (age > 3 * 60 * 1000) {
              logInfo("🔄 Refreshing in background...");
              setTimeout(() => loadFreshData(false), 100);
            }
            return;
          }
        } catch (e) {
          logError("Cache parse error:", e);
        }
      }
    }

    // No cache, show loading for both sections
    isLoading.value = true;
    isLoadingRecommendations.value = true;
    isLoadingAllTutors.value = true;
    error.value = null;
    await loadFreshData();
    isLoading.value = false;
    isLoadingRecommendations.value = false;
    isLoadingAllTutors.value = false;
  } catch (err) {
    logError("❌ Error loading tutors:", err);
    error.value = err.message || "Failed to load tutors";
    recommendedTutorsData.value = [];
    allTutorsData.value = [];
    isLoading.value = false;
    isLoadingRecommendations.value = false;
    isLoadingAllTutors.value = false;
  }
};

// Load fresh data - Recommendations from API, All Tutors from Firestore
const loadFreshData = async (forceRefresh = false) => {
  let currentUser = auth.currentUser;
  if (!currentUser) {
    currentUser = await new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  if (!currentUser) {
    throw new Error("User must be logged in");
  }

  // ===== LOAD RECOMMENDATIONS FROM BACKEND API =====
  const token = await currentUser.getIdToken();
  const timestamp = new Date().getTime();
  const separator = forceRefresh ? "&" : "?";
  const url = `${API_BASE_URL}/tutors/recommendations/${
    forceRefresh ? "?force_refresh=true" : ""
  }${separator}_t=${timestamp}`;

  logInfo(`🌐 Loading recommendations from API: ${url}`);
  logInfo(`🔄 Force Refresh Mode: ${forceRefresh}`);

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const recommendationsData = response.data;
  logInfo(`✅ API Response - Recommendations: ${recommendationsData.recommendations?.length || 0}`);

  // ===== LOAD ALL TUTORS DIRECTLY FROM FIRESTORE =====
  logInfo(`🔥 Loading all tutors from Firestore...`);
  const allTutorsFromFirestore = await loadAllTutorsFromFirestore();
  logInfo(`✅ Firestore Response - All Tutors: ${allTutorsFromFirestore.length}`);

  // Combine data
  const combinedData = {
    recommendations: recommendationsData.recommendations || [],
    allTutors: allTutorsFromFirestore,
    studentSubjects: recommendationsData.studentSubjects || []
  };

  // Cache the combined response
  const CACHE_KEY = "elevateED_tutor_recommendations";
  sessionStorage.setItem(
    CACHE_KEY,
    JSON.stringify({
      data: combinedData,
      timestamp: Date.now(),
    })
  );

  // Populate the data (now async with availability checking)
  await populateTutorData(combinedData);

  logInfo("✅ Loaded tutors:", allTutorsData.value.length);
  logDebug("📚 Student subjects:", studentSubjects.value);
};

// Load all tutors directly from Firestore
const loadAllTutorsFromFirestore = async () => {
  try {
    const tutorsQuery = query(
      collection(db, "users"),
      where("userType", "==", "tutor"),
      where("applicationStatus", "==", "approved")
    );

    const tutorsSnapshot = await getDocs(tutorsQuery);
    const tutors = [];

    for (const tutorDoc of tutorsSnapshot.docs) {
      const tutorData = tutorDoc.data();
      const tutorId = tutorDoc.id;

      // Apply same filters as backend
      const isDocumentsUploaded = tutorData.isDocumentsUploaded || false;
      const isEmailVerified = tutorData.isEmailVerified || false;
      const status = tutorData.status || '';

      // Skip tutors that don't meet requirements
      if (!isDocumentsUploaded || !isEmailVerified || status !== 'Active') {
        continue;
      }

      // Get subjects
      let tutorSubjects = tutorData.subjects || [];
      if (typeof tutorSubjects === 'string') {
        tutorSubjects = tutorSubjects.split(',').map(s => s.trim()).filter(s => s);
      }

      // Get rating and sessions from tutor document (pre-calculated fields)
      const averageRating = tutorData.averageRating || 0;
      const totalSessions = tutorData.totalCompletedSessions || 0;

      // Build tutor object
      tutors.push({
        id: tutorId,
        firstName: tutorData.firstName || '',
        lastName: tutorData.lastName || '',
        middleName: tutorData.middleName || '',
        suffix: tutorData.suffix || '',
        email: tutorData.email || '',
        subjects: tutorSubjects,
        experienceYears: tutorData.experienceYears || 0,
        bio: tutorData.bio || '',
        education: tutorData.education || '',
        languages: tutorData.languages || [],
        profileImage: tutorData.profileImage || '',
        hourlyRate: tutorData.hourlyRate || 0,
        rating: averageRating,
        totalSessions: totalSessions,
        createdAt: tutorData.createdAt || null,
        recommendationScore: 0, // Not calculated for "all tutors" section
        scoreBreakdown: {
          subjectMatch: 0,
          rating: 0,
          experience: 0
        }
      });
    }

    // Sort by createdAt (newest first) to reduce bias toward older tutors
    tutors.sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });

    logInfo(`🔥 Loaded ${tutors.length} tutors from Firestore (sorted newest first)`);
    return tutors;
  } catch (error) {
    logError('❌ Error loading tutors from Firestore:', error);
    return [];
  }
};

// Load tutors on component mount
onMounted(() => {
  setPageMetadata(); // Set SEO metadata
  loadTutors();
  
  // Defer real-time listener to improve initial page load performance
  setTimeout(() => {
    setupRealtimeListener(); // Set up real-time updates after initial render
  }, 1000);
});

// Setup real-time listener for tutor changes
let tutorUnsubscribe = null;
const setupRealtimeListener = () => {
  try {
    // Listen to the users collection for tutors only
    const tutorsQuery = query(
      collection(db, "users"),
      where("role", "==", "Tutor")
    );

    tutorUnsubscribe = onSnapshot(tutorsQuery, (snapshot) => {
      // Check if any changes occurred
      if (!snapshot.metadata.hasPendingWrites && !snapshot.empty) {
        const changes = snapshot.docChanges();
        
        if (changes.length > 0) {
          // Check if any tutors were modified or removed
          const hasRelevantChanges = changes.some(change => 
            change.type === "modified" || change.type === "removed"
          );

          if (hasRelevantChanges) {
            logInfo("🔄 Tutor data changed, invalidating cache...");
            
            // Invalidate cache
            const CACHE_KEY = "elevateED_tutor_recommendations";
            sessionStorage.removeItem(CACHE_KEY);

            // Refresh data silently in background
            loadTutors(false);
          }
        }
      }
    }, (error) => {
      logError("❌ Real-time listener error:", error);
    });

    logInfo("✅ Real-time listener setup complete");
  } catch (error) {
    logError("❌ Failed to setup real-time listener:", error);
  }
};

// Cleanup on component unmount
onUnmounted(() => {
  if (tutorUnsubscribe) {
    tutorUnsubscribe();
    logInfo("🧹 Real-time listener cleaned up");
  }
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
}

.find-tutors-container {
  font-family: "Inter", sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 140px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.main-header {
  text-align: center;
  margin: 50px auto 40px;
  max-width: 1200px;
  padding: 0 20px;
}

.main-title {
  font-size: 36px;
  font-weight: 800;
  color: #000000;
  margin-bottom: 12px;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.main-subtitle {
  font-size: 16px;
  color: #4b5563;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.5;
  white-space: normal;
  padding: 0 20px;
  font-weight: 400;
}
</style>
